import { useRef, useState, useEffect } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, XCircle, Copy } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  useShipmentSession, useScanShipment, type ScanResult,
} from "@/hooks/use-shipment-counting";
import { playSuccess, playError, playDuplicate, playComplete } from "@/lib/sounds";
import { toast } from "sonner";
import { isolateScannerKeys, useScannerFocusGuard } from "@/hooks/use-scanner-input";

type Entry = ScanResult & { at: Date };

const OUTCOME_LABEL = { ok: "U redu", duplicate: "Već skeniran", extra: "Višak — nije u najavi" } as const;

export function ShipmentCountPage() {
  const { sessionId } = useParams({ strict: false }) as { sessionId: string };
  const { data: session } = useShipmentSession(sessionId);
  const scan = useScanShipment(sessionId);
  const [entries, setEntries] = useState<Entry[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const queueRef = useRef<Promise<void>>(Promise.resolve());

  useScannerFocusGuard(inputRef);

  async function doScan(value: string) {
    try {
      const result = await scan.mutateAsync(value);
      if (result.outcome === "ok") {
        playSuccess();
        if (result.progress.missing === 0) playComplete();
      } else if (result.outcome === "duplicate") playDuplicate();
      else playError();
      setEntries((prev) => [{ ...result, at: new Date() }, ...prev].slice(0, 30));
    } catch (err: any) {
      playError();
      toast.error(err.message);
    } finally {
      inputRef.current?.focus();
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const value = e.currentTarget.value.trim();
    e.currentTarget.value = "";
    if (!value) return;
    queueRef.current = queueRef.current.then(() => doScan(value));
  }

  const progress = session?.progress ?? { total: 0, scanned: 0, missing: 0, extra: 0 };
  const pct = progress.total > 0 ? Math.round((progress.scanned / progress.total) * 100) : 0;

  return (
    <div className="max-w-md mx-auto p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Link
          to="/warehouse/brojanje-posiljki/$sessionId"
          params={{ sessionId }}
          className="text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="font-semibold truncate">{session?.name ?? "Brojanje"}</h1>
      </div>

      <div className="text-center border rounded-lg p-4">
        <div className="text-4xl font-bold tabular-nums">
          {progress.scanned} <span className="text-muted-foreground text-2xl">/ {progress.total}</span>
        </div>
        <div className="h-2 bg-muted rounded-full mt-2 overflow-hidden">
          <div className="h-full bg-green-600 transition-all" style={{ width: `${pct}%` }} />
        </div>
        <div className="flex justify-center gap-4 mt-2 text-xs text-muted-foreground">
          <span className={progress.missing > 0 ? "text-destructive" : ""}>Fali: {progress.missing}</span>
          <span className={progress.extra > 0 ? "text-amber-600" : ""}>Višak: {progress.extra}</span>
        </div>
      </div>

      <Input
        ref={inputRef}
        placeholder="Skeniraj broj pošiljke…"
        className="text-lg h-12 font-mono"
        onKeyDown={isolateScannerKeys(handleKeyDown)}
        autoFocus
        autoComplete="off"
      />

      {(session?.manifests?.length ?? 0) > 1 && (
        <div className="space-y-1">
          {session.manifests.map((m: any) => (
            <div key={m.id} className="flex justify-between text-xs text-muted-foreground">
              <span className="truncate">[{m.firmCode}] {m.firmName} — {m.firmAddress}</span>
              <span className="tabular-nums shrink-0 ml-2">{m.scannedCount}/{m.expectedCount}</span>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-1">
        {entries.map((en) => (
          <div
            key={`${en.trackingNumber}-${en.at.getTime()}`}
            className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm ${
              en.outcome === "ok"
                ? "bg-green-500/10 text-green-700 dark:text-green-400"
                : en.outcome === "duplicate"
                  ? "bg-amber-500/10 text-amber-700 dark:text-amber-400"
                  : "bg-red-500/10 text-red-700 dark:text-red-400"
            }`}
          >
            {en.outcome === "ok" ? (
              <CheckCircle2 className="h-4 w-4 shrink-0" />
            ) : en.outcome === "duplicate" ? (
              <Copy className="h-4 w-4 shrink-0" />
            ) : (
              <XCircle className="h-4 w-4 shrink-0" />
            )}
            <span className="font-mono">{en.trackingNumber}</span>
            <span className="truncate text-xs opacity-70">{en.recipient}</span>
            <span className="ml-auto text-xs shrink-0">{OUTCOME_LABEL[en.outcome]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
